/**
 * Lines typed by hand on a quote or invoice (29 Oct 2026): a name, an optional
 * description, a quantity and a unit price, and the amount is the two
 * multiplied. Travel at R7/km for 100 km is quantity 100, unit price 7,
 * amount R700.00. All sums are in integer cents so they never drift.
 *
 * Pure functions, no I/O, like pricing.ts.
 */

export interface EditableLine {
  /** Stable key in the editor; not stored. */
  key: string
  description: string
  detail: string
  quantity: number
  unitPrice: number
  /** Where a line came from (package, add-on, travel, manual). Kept so accepting a quote can fill in deliverables. */
  source: 'package' | 'addon' | 'travel' | 'accommodation' | 'surcharge' | 'discount' | 'manual' | 'vat'
  sourceId: string | null
}

const cents = (rand: number) => Math.round(rand * 100)

/** quantity × unit price, rounded to the cent. */
export function lineAmount(quantity: number, unitPrice: number): number {
  if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return 0
  return Math.round(quantity * unitPrice * 100) / 100
}

export interface LineTotals {
  subtotal: number
  vat: number
  total: number
}

/** Subtotal of the lines, VAT on it when registered, and the total. */
export function lineTotals(lines: Array<Pick<EditableLine, 'quantity' | 'unitPrice'>>, vatRate: number | null): LineTotals {
  const subtotalCents = lines.reduce((sum, line) => sum + cents(lineAmount(line.quantity, line.unitPrice)), 0)
  const vatCents = vatRate ? Math.round(subtotalCents * vatRate) : 0
  return { subtotal: subtotalCents / 100, vat: vatCents / 100, total: (subtotalCents + vatCents) / 100 }
}

/** A line worth saving: it has a name. Empty rows in the editor are skipped. */
export function isFilled(line: Pick<EditableLine, 'description'>): boolean {
  return line.description.trim() !== ''
}

/**
 * A draft invoice's payment plan after its total changed: each instalment
 * keeps its share, and the last absorbs the rounding so they add up to the
 * new total exactly. Instalments that would round to nothing are dropped.
 */
export function rescaleInstalments<T extends { amount: number }>(instalments: T[], newTotal: number): T[] {
  const oldCents = instalments.reduce((sum, item) => sum + cents(item.amount), 0)
  const targetCents = cents(newTotal)
  if (instalments.length === 0 || targetCents <= 0) return []
  if (oldCents <= 0) return [{ ...instalments[instalments.length - 1]!, amount: targetCents / 100 }]

  let given = 0
  const scaled = instalments.map((item, index) => {
    const share =
      index === instalments.length - 1 ? targetCents - given : Math.round((cents(item.amount) * targetCents) / oldCents)
    given += share
    return { ...item, amount: share / 100 }
  })
  return scaled.filter((item) => item.amount > 0)
}
